import { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const LEGACY_PORTAL_BASE = (process.env.REACT_APP_LEGACY_PORTAL_BASE || "http://127.0.0.1").replace(/\/$/, "");

export default function DepartmentsCentres() {
  const [faculties, setFaculties] = useState([]);
  const [activeFacultyId, setActiveFacultyId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasDepartments = useMemo(() => departments.length > 0, [departments]);

  useEffect(() => {
    const fetchFaculties = async () => {
      try {
        const res = await fetch(`${API_ROOT}/api/infra/faculties`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch faculties");
        }

        setFaculties(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch faculties");
      }
    };

    fetchFaculties();
  }, []);

  const viewDepartmentsForFaculty = async (depttfcaultyid) => {
    try {
      setLoading(true);
      setError("");
      setActiveFacultyId(String(depttfcaultyid));

      const res = await fetch(
        `${API_ROOT}/api/infra/departments?depttfcaultyid=${encodeURIComponent(depttfcaultyid)}`
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to load Department list. Please try again.");
      }

      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      setDepartments([]);
      setError(err.message || "Failed to load Department list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Departments and Centres</h3>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white fw-semibold">Faculties</div>
            <div className="table-responsive">
              <table className="table table-bordered table-striped mb-0">
                <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
                  <tr>
                    <th style={{ width: "72px" }}>SNo</th>
                    <th>Department</th>
                  </tr>
                </thead>
                <tbody>
                  {faculties.map((row, idx) => (
                    <tr key={row.DepttFacultyID}>
                      <td>{idx + 1}</td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-link p-0 text-start ${
                            String(row.DepttFacultyID) === activeFacultyId ? "fw-bold" : ""
                          }`}
                          onClick={() => viewDepartmentsForFaculty(row.DepttFacultyID)}
                        >
                          {row.DepttFaculty}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!faculties.length && (
                    <tr>
                      <td colSpan={2} className="text-center text-muted">
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white fw-semibold">Department List</div>
            <div className="table-responsive">
              <table className="table table-bordered table-striped mb-0">
                <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
                  <tr>
                    <th style={{ width: "72px" }}>SNo</th>
                    <th>Department Name</th>
                    <th>Department Head</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">Loading...</td>
                    </tr>
                  )}
                  {!loading && hasDepartments &&
                    departments.map((row, idx) => {
                      const folder = (row.Folder || "").trim();
                      const deptFile = folder ? `${LEGACY_PORTAL_BASE}/${folder}.php` : "#";

                      return (
                        <tr key={`${folder}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>
                            {folder ? (
                              <a href={deptFile} className="menu-link">
                                {row.Department}
                              </a>
                            ) : (
                              row.Department
                            )}
                          </td>
                          <td>{row.HeadName}</td>
                        </tr>
                      );
                    })}
                  {!loading && !hasDepartments && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">
                        Select a faculty to view departments.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
